import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FilterListSharpIcon from "@mui/icons-material/FilterListSharp";
import FormatPaintOutlinedIcon from "@mui/icons-material/FormatPaintOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import DoDisturbAltOutlinedIcon from "@mui/icons-material/DoDisturbAltOutlined";
import { DeleteOutlineOutlined } from "@mui/icons-material";
import ResolveInstallation from "../../LandingPages/ResolveInstallation.jsx";
import CapitalizeFirstLetter from "../../../shared/utils/CapitalizeFirstLetter.jsx";

import AddFeedbackModal from "./AddFeedbackModal";
import Skeleton from "@mui/material/Skeleton";
import axios from "axios";
import Decrypt from "../../../shared/utils/Decrypt";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import { Snackbar, Alert, IconButton } from "@mui/material";

import VendorMachineCard from "./VendorMachineCard";
import styles from "../../../styles/vendorPurchaseOrder.module.css";
import { Dialog } from "@mui/material";

const rateCards = [
  {
    id: 1,
    title: "National Retail Branding Q4",
    status: "ONGOING",
    updatedAt: "Oct 12, 2024",
    updatedBy: "Rohit Kumar",
    products: [
      {
        name: "Glow Sign Board",
        description: "3mm ACP with 5mm Acrylic letters",
        uom: "Sq.ft",
        rate: 240,
      },
      {
        name: "ACP Backlit 3D Letters",
        description: "High precision finish, edge polished",
        uom: "Sq.ft",
        rate: 1200,
      },
      {
        name: "Non Lit Board",
        description: "MS square pipe, 1x1 inch",
        uom: "Sq.ft",
        rate: 95,
      },
    ],
  },

  {
    id: 2,
    title: "Summer In-Store Signage 2024",
    status: "ACTIVE",
    updatedAt: "Apr 05, 2024",
    updatedBy: "Ankush Singh",
    products: [
      {
        name: "Vinyl Printing + Sunboard Mounting",
        description: "5mm Sunboard with high-res matt vinyl",
        uom: "Sq.ft",
        rate: 85,
      },
      {
        name: "Acrylic Leaflet Holder",
        description: "A4 size, transparent, counter-top",
        uom: "Unit",
        rate: 340,
      },
      {
        name: "Floor Standee",
        description: "Eco solvent print with stand",
        uom: "Unit",
        rate: 650,
      },
    ],
  },

  {
    id: 3,
    title: "Festive Season 2023",
    status: "COMPLETED",
    updatedAt: "Jan 10, 2024",
    updatedBy: "Mayank Gupta",
    products: [
      {
        name: "LED Decorative Signage",
        description: "Warm white festive branding",
        uom: "Sq.ft",
        rate: 380,
      },
      {
        name: "Hanging Danglers",
        description: "Double side printed danglers",
        uom: "Unit",
        rate: 55,
      },
      {
        name: "Window Branding",
        description: "One way vision vinyl",
        uom: "Sq.ft",
        rate: 140,
      },
    ],
  },
];

function StatusBadge({ label }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
      {label}
    </span>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase">
        {label}
      </span>
      <span className="text-sm font-medium text-slate-800">{value || "—"}</span>
    </div>
  );
}

export default function VendorViewRoute() {
  const navigate = useNavigate();
  const role = Number(localStorage.getItem("role"));
  const [activeTab, setActiveTab] = useState("Overview");
  const [showAllFeedbacks, setShowAllFeedbacks] = useState(false);
  const [expanded, setExpanded] = useState([1]);
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [vendorData, setVendorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [allComments, setAllComments] = useState([]);
  const [tone, setTone] = useState("Positive");
  const [isEditingReason, setIsEditingReason] = useState(false);
  const [editReason, setEditReason] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [campaignPosData, setCampaignPosData] = useState([]);
  const [selectedPOs, setSelectedPOs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [resolvedFlagModal, setResolvedFlagModal] = useState(false);
  const [flagData, setFlagData] = useState(null);
  const serverURL = process.env.REACT_APP_URL;
  const tabs = [
    "Overview",
    "Capabilities",
    // "Rates",
    ...([-1, 3].includes(Number(role)) ? ["History"] : []),
    "Feedback & Document",
    ...([-1, 0, 3, 4].includes(Number(role)) ? ["Installation Flag"] : []),
  ];

  const formatIndianDateTime = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour12: true,
    });
  };

  const handleCloseResolvedFlag = () => {
    setResolvedFlagModal(false);
    setFlagData(null);
  };

  const handleViewPOs = (project) => {
    const pos = project?.pos;

    navigate(`/adminportal/vendorDetails/${id}/purchaseOrder`, {
      state: {
        documents: pos,
        vendorId: id,
      },
    });
  };

  const { id } = useParams();
  useEffect(() => {
    if (!id) return;

    fetchVendor();
  }, [id]);

  useEffect(() => {
    if (activeTab === "Installation Flag" && id) {
      fetchVendorTickets();
    }
  }, [activeTab, id]);

  const ROLE_MAP = {
    "-1": "Super Admin",
    0: "Csm",
    1: "Business Development",
    2: "Management Trainee",
    3: "Purchase",
    4: "Operations Manager",
    5: "IP Manager",
    6: "Store Onboarding Executive",
    7: "Designer Manager",
  };
  const reasonStatusConfig = {
    1: {
      title: "Vendor Approved",
      subtitle: vendorData?.adminApprovalReason,
      icon: "✅",
      border: "border-green-200",
      header: "bg-green-50 border-green-100",
      titleColor: "text-green-700",
      subColor: "text-green-500",
      buttonColor: "text-green-600",
      saveColor: "bg-green-600",
    },
    "-1": {
      title: "Vendor Rejected",
      subtitle: vendorData?.adminApprovalReason,
      icon: "❌",
      border: "border-orange-200",
      header: "bg-orange-50 border-orange-100",
      titleColor: "text-orange-700",
      subColor: "text-orange-500",
      buttonColor: "text-orange-600",
      saveColor: "bg-orange-600",
    },
    "-2": {
      title: "Vendor Blacklisted",
      subtitle: vendorData?.reasonForBlacklisted,
      icon: "🚫",
      border: "border-red-200",
      header: "bg-red-50 border-red-100",
      titleColor: "text-red-700",
      subColor: "text-red-500",
      buttonColor: "text-red-600",
      saveColor: "bg-red-600",
    },
  };

  const statusInfo = reasonStatusConfig[String(vendorData?.adminApproval)];

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchVendorTickets = async () => {
    setTicketsLoading(true);

    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${serverURL}/installation-flags/vendor/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      console.log("response", response);

      if (response?.data?.success) {
        setTickets(response?.data?.data || []);
      } else {
        setTickets([]);
      }
    } catch (error) {
      console.error("Error fetching installation tickets:", error);
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  };

  const fetchVendor = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${serverURL}/api/vendors/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = response.data;

      const campaignMap = data.campaigns.reduce((acc, campaign) => {
        const campaignData = campaign?.campaignId;

        if (!campaignData?._id) return acc;

        const campaignId = campaignData._id;

        if (!acc[campaignId]) {
          acc[campaignId] = {
            campaignId,
            campaignTitle: campaignData.campaignTitle,
            campaignOwnerName: campaignData.campaignOwnerId?.name,
            startDate: campaignData.startDate,
            status: campaignData.status,
            totalPOValue: 0,
            posCount: 0,
            pos: [],
          };
        }

        if (campaign.purchaseOrderId) {
          const po = campaign.purchaseOrderId;

          const poValue = Number(po?.rawData?.grand_total) || 0;

          acc[campaignId].pos.push(po);
          acc[campaignId].totalPOValue += poValue;
          acc[campaignId].posCount += 1;
        }

        return acc;
      }, {});

      setCampaignPosData(Object.values(campaignMap));

      // setTickets(data.tickets);

      const decryptedVendor = {
        ...data,
        GSTIN: Decrypt(data.GSTIN),
        ownerName: Decrypt(data.ownerName),
        ownerPrimaryContactNo: Decrypt(data.ownerPrimaryContactNo),
        ownerSecondaryContactNo: Decrypt(data.ownerSecondaryContactNo),
      };

      setVendorData(decryptedVendor);
      setAllComments(data.comments);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const visibleFeedbacks = showAllFeedbacks
    ? allComments
    : allComments.slice(0, 3);
  const handleAccordion = (id) => {
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };
  const getVendorStatusConfig = (status) => {
    switch (status) {
      case 1:
        return {
          bg: "bg-green-500",
          icon: <VerifiedIcon fontSize="small" />,
        };

      case -2:
        return {
          bg: "bg-red-500",
          icon: <DoDisturbAltOutlinedIcon fontSize="small" />,
        };

      case -1:
        return {
          bg: "bg-orange-500",
          icon: <ThumbDownAltOutlinedIcon fontSize="small" />,
        };

      default:
        return {
          bg: "bg-yellow-500",
          icon: <PendingActionsOutlinedIcon fontSize="small" />,
        };
    }
  };
  const getBadgeConfig = (badge) => {
    switch (badge?.toLowerCase()) {
      case "platinum":
        return {
          text: "PLATINUM",
          bg: "bg-blue-100",
          textColor: "text-blue-700",
        };

      case "gold":
        return {
          text: "GOLD",
          bg: "bg-amber-100",
          textColor: "text-amber-700",
        };

      case "silver":
        return {
          text: "SILVER",
          bg: "bg-gray-100",
          textColor: "text-gray-700",
        };

      default:
        return {
          text: "UNRATED",
          bg: "bg-slate-200",
          textColor: "text-slate-700",
        };
    }
  };
  const fetchComments = async () => {
    if (!vendorData?._id) return;
    try {
      const response = await axios.get(
        `${serverURL}/api/viewComment/${vendorData._id}`,

        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.data.success) {
        setAllComments(response.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleFeedbackSubmit = async (feedback) => {
    if (!feedback.trim()) {
      setSnackbar({
        open: true,
        message: "Feedback cannot be empty",
        severity: "warning",
      });
      return;
    }

    try {
      if (editingCommentId) {
        setCommentLoading(true);
        await axios.put(
          `${serverURL}/api/editComment/${editingCommentId}`,
          {
            comment: feedback,
            userId: localStorage.getItem("userId"),
            commentTone: tone,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
      } else {
        setCommentLoading(true);
        await axios.post(
          `${serverURL}/api/comment/${vendorData._id}`,
          {
            comment: feedback,
            userId: localStorage.getItem("userId"),
            commentTone: tone,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
      }

      fetchComments();

      setFeedback("");
      setEditingCommentId(null);
      setOpenFeedbackModal(false);
      setCommentLoading(false);
      setTone("Positive");
    } catch (error) {
      console.error(error);

      setSnackbar({
        open: true,
        message: "Failed to add feedback",
        severity: "error",
      });
    } finally {
      setCommentLoading(false);
    }
  };

  const handleEditComment = (item) => {
    setEditingCommentId(item._id);
    setFeedback(item.comment);
    setTone(item.commentTone);
    setOpenFeedbackModal(true);
  };

  const handleDeleteComment = async (item) => {
    console.log(item);
    setCommentLoading(true);
    try {
      await axios.delete(
        `${serverURL}/installationPartner/deleteComment/${item._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      fetchComments();
      setCommentLoading(false);
    } catch (error) {
      console.error(error);
    }
  };
  const handleUpdateReason = async () => {
    try {
      const token = localStorage.getItem("token");

      const payLoad = {
        status: vendorData?.adminApproval,
        reason: editReason,
      };
      await axios.put(
        `${serverURL}/api/updateStatus/${vendorData._id}`,
        payLoad,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      await fetchVendor();

      setIsEditingReason(false);
    } catch (error) {
      console.error(error);
    }
  };
  const badgeConfig = getBadgeConfig(vendorData?.badge);

  const statusConfig = getVendorStatusConfig(vendorData?.adminApproval);
  return (
    <div className="h-[93vh] w-full overflow-auto bg-white">
      <div className="px-10 py-5 space-y-4">
        {/* Vendor Header */}
        {loading ? (
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <Skeleton variant="rounded" width={132} height={132} />

            <div className="space-y-3">
              <Skeleton width={250} height={40} />
              <Skeleton width={120} height={30} />
              <Skeleton width={300} height={25} />
              <Skeleton width={180} height={25} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-center  gap-5">
            <div className="relative">
              <div className="h-32 w-32 sm:h-[132px] sm:w-[132px] overflow-hidden rounded-xl border-4 border-white shadow-sm">
                <img
                  src={
                    vendorData?.profilePhoto
                      ? vendorData?.profilePhoto
                      : "/assests/AdminDashboard/userForIP.webp"
                  }
                  alt="Vendor Photo"
                  className="h-full w-full object-contain"
                />
              </div>

              <div
                className={`absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-md border-2 border-white text-white shadow-md ${statusConfig.bg}`}
              >
                {statusConfig.icon}
              </div>
            </div>

            <div className="text-center sm:text-left">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
                <h1 className="text-2xl sm:text-[28px] font-bold text-[#163D8F]">
                  {[-1, 0, 2, 3, 4].includes(role)
                    ? vendorData?.vendorBusinessName
                    : "**********"}
                </h1>

                <span
                  className={`rounded-full px-4 py-1 text-xs font-bold uppercase ${badgeConfig.bg} ${badgeConfig.textColor}`}
                >
                  {badgeConfig.text}
                </span>
              </div>

              <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:gap-8 text-[#4B5563]">
                <span>
                  {" "}
                  <LocationOnOutlinedIcon /> {vendorData?.state},{" "}
                  {vendorData?.city}
                </span>
                <span className="flex items-center gap-2">
                  {vendorData?.isPaintingVendorSelected ? (
                    <>
                      <FormatPaintOutlinedIcon />
                      Painting
                    </>
                  ) : (
                    <>
                      <PrecisionManufacturingOutlinedIcon />
                      Fabrication & Printing
                    </>
                  )}
                </span>
              </div>

              <div className="mt-4 flex items-center justify-center sm:justify-start gap-2 text-[#4B5563]">
                {vendorData?.adminApproval === 1 ? (
                  <>
                    <CheckCircleOutlinedIcon /> Verified Vendor
                  </>
                ) : vendorData?.adminApproval === -2 ? (
                  <>
                    <DoDisturbAltOutlinedIcon /> BlackListed Vendor
                  </>
                ) : vendorData?.adminApproval === -1 ? (
                  <>
                    <ThumbDownAltOutlinedIcon /> Rejected Vendor
                  </>
                ) : (
                  <>
                    <PendingActionsOutlinedIcon /> Pending Vendor
                  </>
                )}{" "}
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-[#D4D8E1] overflow-x-auto">
          <div className="flex min-w-max gap-8 sm:gap-16">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`relative whitespace-nowrap py-5 text-sm sm:text-base font-medium ${
                  activeTab === tab ? "text-[#163D8F]" : "text-[#4B5563]"
                }`}
              >
                {tab}

                {activeTab === tab && (
                  <div className="absolute bottom-0 left-0 h-[2px] w-full bg-[#163D8F]" />
                )}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "Overview" && (
          <>
            {loading ? (
              <>
                {/* Basic Info + Performance */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  <div className="lg:col-span-8">
                    <div className="rounded-xl border p-8">
                      <Skeleton width={200} height={35} />

                      <div className="grid grid-cols-2 gap-10 mt-8">
                        {[...Array(6)].map((_, index) => (
                          <div key={index}>
                            <Skeleton width={120} height={20} />
                            <Skeleton width={180} height={30} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-4">
                    <Skeleton variant="rounded" height={350} />
                  </div>
                </div>

                {/* Contact */}
                <Skeleton variant="rounded" height={220} />

                {/* Other Info */}
                <Skeleton variant="rounded" height={300} />
              </>
            ) : (
              <>
                {/* Basic Info + Performance */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  {/* Basic Information */}
                  <div className="lg:col-span-8">
                    <div className="overflow-hidden rounded-xl border border-[#D4D8E1] bg-white shadow-sm">
                      {/* Header */}
                      <div className="flex items-center justify-between border-b border-[#D4D8E1] bg-gray-100 px-8 py-5">
                        <h2 className="text-[18px] font-semibold text-[#1F2937]">
                          Basic Information
                        </h2>

                        <span className="text-sm font-medium text-[#6B7280]">
                          VND-{vendorData?._id?.slice(-5)?.toUpperCase() || "-"}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="grid grid-cols-2 gap-x-20 gap-y-10 px-8 py-8 ">
                        <div>
                          <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                            GSTIN
                          </p>
                          <p className="mt-2 text-[16px] text-[#1F2937]">
                            {[-1, 0, 2, 3, 4].includes(role)
                              ? vendorData?.GSTIN || "-"
                              : "**********"}
                          </p>
                        </div>

                        <div>
                          <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                            Company Type
                          </p>
                          <p className="mt-2 text-[16px] text-[#1F2937]">
                            {vendorData?.typeOfBusiness || "-"}
                          </p>
                        </div>

                        <div>
                          <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                            Business Status
                          </p>

                          <div className="mt-2 flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full bg-[#57D38C]" />
                            <span className="text-[16px] text-[#1F2937]">
                              {vendorData?.status || "-"}
                            </span>
                          </div>
                        </div>

                        <div>
                          <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                            Business Owner Name
                          </p>
                          <p className="mt-2 text-[16px] text-[#1F2937]">
                            {[-1, 0, 2, 3, 4].includes(role)
                              ? vendorData?.ownerName || "-"
                              : "**********"}
                          </p>
                        </div>

                        <div>
                          <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                            Onboarded By
                          </p>
                          <p className="mt-2 text-[16px] text-[#1F2937]">
                            {vendorData?.addedBy?.name || "-"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                            Pin Code
                          </p>
                          <p className="mt-2 text-[16px] text-[#1F2937]">
                            {vendorData?.pinCode || "-"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                            Business Location
                          </p>
                          <p className="mt-2 text-[16px] text-[#1F2937]">
                            {vendorData?.businessLocation || "-"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                            Website
                          </p>
                          <p className="mt-2 text-[16px] text-[#1F2937]">
                            {vendorData?.website || "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Stability Metrics */}
                  <div className="lg:col-span-4">
                    <div className="h-full rounded-xl bg-[#24439A] p-8 text-white shadow-sm">
                      <h3 className="text-[18px] font-medium">
                        Stability Metrics
                      </h3>

                      <div className="mt-12">
                        <p className="text-[12px] uppercase tracking-wide text-white/70">
                          Reliability Score
                        </p>

                        <div className="mt-2 flex items-end">
                          <span className="text-[56px] font-semibold leading-none">
                            {vendorData?.reliability || "-"}
                          </span>

                          <span className="mb-2 text-[20px] text-white/70">
                            /5
                          </span>
                        </div>
                      </div>

                      <div className="mt-16 grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-[12px] uppercase tracking-wide text-white/70">
                            Experience
                          </p>

                          <h4 className="mt-2 text-[18px] font-semibold">
                            {vendorData?.yearsInBusiness || "-"}
                            Years
                          </h4>
                        </div>

                        <div>
                          <p className="text-[12px] uppercase tracking-wide text-white/70">
                            Staff
                          </p>

                          <h4 className="mt-2 text-[18px] font-semibold">
                            {vendorData?.numberOfEmployees || "-"}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* contact & headquarters */}
                <div className="overflow-hidden rounded-xl border border-[#D4D8E1] bg-white shadow-sm">
                  {/* Header */}
                  <div className="border-b border-[#D4D8E1] px-8 py-5 bg-gray-100">
                    <h2 className="text-[18px] font-semibold text-[#1F2937]">
                      Contact & Headquarters
                    </h2>
                  </div>

                  {/* Content */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 px-8 py-8">
                    {/* Primary Contact */}
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#F3F4F6]">
                        <CallOutlinedIcon />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                          Primary Contact No.
                        </p>

                        {/* Primary Contact */}
                        <p className="mt-1 text-[16px] text-[#1F2937]">
                          {[-1, 0, 2, 3, 4].includes(role)
                            ? vendorData?.ownerPrimaryContactNo || "-"
                            : "**********"}
                        </p>
                      </div>
                    </div>

                    {/* Secondary Contact */}
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#F3F4F6]">
                        <CallOutlinedIcon />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                          Secondary Contact No.
                        </p>

                        <p className="mt-1 text-[16px] text-[#1F2937]">
                          {[-1, 0, 2, 3, 4].includes(role)
                            ? vendorData?.ownerSecondaryContactNo || "—"
                            : "**********"}
                        </p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#F3F4F6]">
                        <EmailOutlinedIcon />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                          Business Email
                        </p>

                        <p className="mt-1 text-[16px] text-[#1F2937]">
                          {[-1, 0, 2, 3, 4].includes(role)
                            ? vendorData?.emailAddress || "-"
                            : "**********"}
                        </p>
                      </div>
                    </div>

                    {/* Address */}
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#F3F4F6]">
                        <LocationOnOutlinedIcon />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                          Address
                        </p>

                        <p className="mt-1 text-[16px] leading-7 text-[#1F2937]">
                          {[-1, 0, 2, 3, 4].includes(role)
                            ? vendorData?.address || "-"
                            : "**********"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* other information */}
                <div className="overflow-hidden rounded-xl border border-[#D4D8E1] bg-white shadow-sm">
                  {/* Header */}
                  <div className="border-b border-[#D4D8E1] px-8 py-5 bg-gray-100">
                    <h2 className="text-[18px] font-semibold text-[#1F2937]">
                      Other Information
                    </h2>
                  </div>

                  {/* Contact */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-12 px-8 py-8">
                    <div>
                      <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                        No. Of Skilled Manpower
                      </p>
                      <p className="mt-2 text-[16px] text-[#1F2937]">
                        {vendorData?.numberOfSkilledManpower || "-"}
                      </p>
                    </div>

                    <div>
                      <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                        No. Of Unskilled Manpower
                      </p>
                      <p className="mt-2 text-[16px] text-[#1F2937]">
                        {vendorData?.numberOfUnskilledManpower || "-"}
                      </p>
                    </div>

                    <div>
                      <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                        Registered In ESI/PF
                      </p>
                      <p className="mt-2 text-[16px] text-[#1F2937]">
                        {vendorData?.registeredInESIPF ? "Yes" : "No"}
                      </p>
                    </div>

                    {[-1, 0, 2, 3, 4].includes(role) && (
                      <div>
                        <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                          Bank Account Number
                        </p>
                        <p className="mt-2 text-[16px] text-[#1F2937]">
                          {vendorData?.bankAccountNumber || "-"}
                        </p>
                      </div>
                    )}

                    {[-1, 0, 2, 3, 4].includes(role) && (
                      <div>
                        <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                          Bank Name
                        </p>
                        <p className="mt-2 text-[16px] text-[#1F2937]">
                          {vendorData?.bankName || "-"}
                        </p>
                      </div>
                    )}

                    {[-1, 0, 2, 3, 4].includes(role) && (
                      <div>
                        <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                          IFSC Code
                        </p>
                        <p className="mt-2 text-[16px] text-[#1F2937]">
                          {vendorData?.bankIFSCCode || "-"}
                        </p>
                      </div>
                    )}

                    <div>
                      <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                        Avg. Turnover In Lakhs
                      </p>
                      <p className="mt-2 text-[16px] text-[#1F2937]">
                        {vendorData?.avgTurnoverLast3Years || "-"}
                      </p>
                    </div>

                    <div>
                      <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                        Production Area In Sq. Ft.
                      </p>
                      <p className="mt-2 text-[16px] text-[#1F2937]">
                        {vendorData?.productionArea || "-"}
                      </p>
                    </div>

                    <div>
                      <p className="text-[13px] font-bold uppercase tracking-wide text-[#4B5563]">
                        Factors That Will Help In Scale
                      </p>
                      <p className="mt-2 text-[16px] text-[#1F2937]">
                        {vendorData?.scalingFactors?.join(",") || "-"}
                      </p>
                    </div>
                  </div>
                </div>
                {/* other images */}

                <div className="overflow-hidden rounded-xl border border-[#D4D8E1] bg-white shadow-sm">
                  {/* Header */}
                  <div className="border-b border-[#D4D8E1] px-8 py-5 bg-gray-100">
                    <h2 className="text-[18px] font-semibold text-[#1F2937]">
                      Document & Certificates
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Cancelled Cheque */}
                    {[-1, 0, 2, 3, 4].includes(role) &&
                      vendorData?.chequeBookImageURL && (
                        <div className="rounded-xl border border-[#D4D8E1] bg-white overflow-hidden m-4">
                          <div className="p-5">
                            <h3 className="mb-4 text-[16px] font-semibold text-[#1F2937]">
                              Cancelled Cheque
                            </h3>

                            <img
                              src={vendorData.chequeBookImageURL}
                              alt="Cancelled Cheque"
                              onClick={() =>
                                setSelectedImage(vendorData.chequeBookImageURL)
                              }
                              className="h-[180px] w-full cursor-pointer rounded-lg object-cover"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                            <div
                              style={{ display: "none" }}
                              className="h-[180px] w-full items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm font-medium text-slate-400"
                            >
                              No Image Available
                            </div>
                          </div>
                        </div>
                      )}
                    {/* Certificate */}
                    {vendorData?.certifications?.map((img, index) => (
                      <div
                        key={index}
                        className="rounded-xl border border-[#D4D8E1] bg-white overflow-hidden m-4"
                      >
                        <div className="p-5">
                          <h3 className="mb-4 text-[16px] font-semibold text-[#1F2937]">
                            Certificate {index + 1}
                          </h3>
                          <img
                            src={img}
                            alt="Certificate"
                            onClick={() => setSelectedImage(img)}
                            className="h-[180px] w-full cursor-pointer rounded-lg object-cover"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "flex";
                            }}
                          />

                          <div
                            style={{ display: "none" }}
                            className="h-[180px] w-full items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm font-medium text-slate-400"
                          >
                            No Image Available
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Vendor Images */}
                    {vendorData?.vendorimagesUploaded?.map((img, index) => (
                      <div
                        key={index}
                        className="rounded-xl border border-[#D4D8E1] bg-white overflow-hidden m-4"
                      >
                        <div className="p-5">
                          <h3 className="mb-4 text-[16px] font-semibold text-[#1F2937]">
                            Vendor Image {index + 1}
                          </h3>

                          <img
                            src={img}
                            alt="Certificate"
                            onClick={() => setSelectedImage(img)}
                            className="h-[180px] w-full cursor-pointer rounded-lg object-cover"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "flex";
                            }}
                          />

                          <div
                            style={{ display: "none" }}
                            className="h-[180px] w-full items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm font-medium text-slate-400"
                          >
                            No Image Available
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {vendorData?.adminApproval !== 0 && (
                  <div
                    className={`mb-6 overflow-hidden rounded-2xl border ${statusInfo.border} bg-white shadow-sm`}
                  >
                    <div
                      className={`flex items-center justify-between border-b px-6 py-4 ${statusInfo.header}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white">
                          {statusInfo.icon}
                        </div>

                        <div>
                          <h3
                            className={`font-semibold ${statusInfo.titleColor}`}
                          >
                            {statusInfo.title}
                          </h3>
                        </div>
                      </div>

                      {role == -1 && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditReason(
                              vendorData?.adminApprovalReason || "",
                            );
                            setIsEditingReason(true);
                          }}
                        >
                          <ModeEditOutlinedIcon
                            className={statusInfo.buttonColor}
                            fontSize="small"
                          />
                        </IconButton>
                      )}
                    </div>

                    <div className="p-6">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Reason
                      </p>

                      {isEditingReason ? (
                        <>
                          <textarea
                            rows={4}
                            value={editReason}
                            onChange={(e) => setEditReason(e.target.value)}
                            className="w-full rounded-xl border border-slate-300 p-3"
                          />

                          <div className="mt-3 flex justify-end gap-2">
                            <button
                              onClick={() => setIsEditingReason(false)}
                              className="rounded-lg border px-4 py-2 text-sm"
                            >
                              Cancel
                            </button>

                            <button
                              onClick={handleUpdateReason}
                              className={`rounded-lg px-4 py-2 text-sm text-white ${statusInfo.saveColor}`}
                            >
                              Save
                            </button>
                          </div>
                        </>
                      ) : (
                        <p className="rounded-xl bg-slate-50 p-4 leading-7 text-slate-700">
                          {statusInfo.subtitle || "No reason provided."}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
        {activeTab === "Capabilities" && (
          <>
            {/* Fabrication Infrastructure */}
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-[20px] font-semibold text-[#163D8F]">
                  Fabrication Infrastructure
                </h2>

                <span className="text-[16px] font-medium text-[#163D8F]">
                  {vendorData?.fabricationMachines?.length || "-"} Machines
                </span>
              </div>

              {/* Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {vendorData?.fabricationMachines.map((machine) => (
                  <VendorMachineCard
                    machine={machine}
                    type="Fabrication"
                    handleSelectImage={(image) => {
                      setSelectedImage(image);
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Printing Machinery & Infrastructure */}

            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-[20px] font-semibold text-[#163D8F]">
                  Printing Machinery & Infrastructure
                </h2>

                <span className="text-[16px] font-medium text-[#163D8F]">
                  {vendorData?.printingMachines?.length || "-"} Machines
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {vendorData?.printingMachines?.map((machine) => (
                  <VendorMachineCard
                    machine={machine}
                    type="Printing"
                    handleSelectImage={(image) => {
                      setSelectedImage(image);
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Product Rates */}
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-[20px] font-semibold text-[#163D8F]">
                  Product Rates
                </h2>

                <span className="text-[16px] font-medium text-[#163D8F]">
                  {vendorData?.productRate?.length || "-"} Products
                </span>
              </div>

              {/* Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {vendorData?.productRate?.map((product) => (
                  <div
                    key={product._id}
                    className="overflow-hidden rounded-xl border border-[#D4D8E1] bg-white transition-all hover:shadow-md"
                  >
                    <div className="p-6">
                      {/* Top */}
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-[18px] font-semibold text-[#1F2937]">
                            {product?.productType}
                          </h3>
                        </div>

                        <div className="rounded-lg bg-[#EEF4FF] p-2">
                          <FilterListSharpIcon
                            className="text-[#163D8F]"
                            fontSize="small"
                          />
                        </div>
                      </div>

                      {/* Rate */}
                      <div className="mt-8">
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                          Settled Rate
                        </p>

                        <div className="mt-2 flex items-end gap-1">
                          <span className="text-4xl font-bold text-[#163D8F]">
                            ₹{product?.rate}
                          </span>

                          <span className="mb-1 text-sm text-slate-500">
                            / {product?.unit}
                          </span>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="mt-8 border-t border-slate-200 pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-slate-400">
                              Unit
                            </p>

                            <p className="font-medium text-slate-700">
                              {product?.unit}
                            </p>
                          </div>

                          <div>
                            <p className="text-xs uppercase tracking-wide text-slate-400">
                              Category
                            </p>

                            <p className="font-medium text-slate-700">
                              {product?.category}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        {activeTab === "History" && (
          <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-[20px] font-semibold text-[#163D8F]">
                Project History
              </h2>

              {/* <button className="flex items-center gap-2 rounded-lg border border-[#D4D8E1] bg-white px-4 py-2 text-sm font-medium text-[#374151] hover:bg-gray-50">
                <FilterListSharpIcon size={16} />
                Filter
              </button> */}
            </div>

            {/* Table */}
            <div className=" rounded-xl border h-[calc(100vh-200px)] overflow-auto border-[#D4D8E1] bg-white">
              <div className="">
                <table className={styles.campaignTable}>
                  <thead>
                    <tr>
                      <th className={styles.campaignTableHeader}>
                        Campaign ID
                      </th>
                      <th className={styles.campaignTableHeader}>
                        Campaign Name
                      </th>
                      <th className={styles.campaignTableHeader}>
                        {" "}
                        Total Sq. Ft.
                      </th>

                      <th className={styles.campaignTableHeader}>
                        {" "}
                        Total Value
                      </th>
                      <th className={styles.campaignTableHeaderCenter}>
                        Purchase Order
                      </th>
                      <th className={styles.campaignTableHeaderCenter}>
                        Status
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {campaignPosData.length > 0 ? (
                      campaignPosData.map((project, index) => {
                        const totalPrice = project?.totalPOValue;

                        return (
                          <tr key={index} className={styles.campaignTableRow}>
                            {/* NEW: Campaign ID cell */}
                            <td className={styles.campaignTableCell}>
                              <h3 className="text-[16px] font-medium text-[#1F2937]">
                                {project?.campaignId
                                  ? `CMP-${project.campaignId.slice(-6).toUpperCase()}`
                                  : "-"}
                              </h3>
                            </td>

                            <td className={styles.campaignTableCell}>
                              <div>
                                <h3 className="text-[16px] font-medium text-[#1F2937]">
                                  {project?.campaignTitle}
                                </h3>

                                <p className="mt-1 text-sm text-[#6B7280]">
                                  {project?.campaignOwnerName || "N/A"}
                                </p>
                              </div>
                            </td>

                            <td className={styles.campaignTableArea}>
                              {project?.totalArea || 0}
                            </td>

                            <td className={styles.campaignTableValue}>
                              {Number(totalPrice || 0).toFixed(2)}
                            </td>

                            <td className={styles.campaignTableCellCenter}>
                              {project?.pos?.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => handleViewPOs(project)}
                                  className="inline-flex items-center gap-2 rounded-lg bg-[#163D8F] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#0F2E72]"
                                >
                                  View PO : {project?.posCount || 0}
                                </button>
                              ) : (
                                <span className="text-sm   text-[#9CA3AF]">
                                  Not Available
                                </span>
                              )}
                            </td>

                            <td className={styles.campaignTableCellCenter}>
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
                                  project.status === "completed"
                                    ? "bg-[#A7F3D0] text-[#047857]"
                                    : "bg-[#DBEAFE] text-[#4B5563]"
                                }`}
                              >
                                {CapitalizeFirstLetter(project?.status)}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={8} className="px-6 py-16">
                          <div className="flex flex-col items-center justify-center text-center">
                            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-3xl">
                              📂
                            </div>

                            <h3 className="text-lg font-semibold text-slate-700">
                              No Project History Found
                            </h3>

                            <p className="mt-2 max-w-md text-sm text-slate-500">
                              This vendor has not been associated with any
                              completed or ongoing projects yet.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        {activeTab === "Feedback & Document" && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div>
                <h2 className="text-xl font-semibold text-[#163D8F]">
                  Feedback & Documents
                </h2>
                <p className="text-sm text-slate-500">
                  Internal reviews from procurement and operations teams.
                </p>
              </div>

              {role == -1 && (
                <button
                  className="rounded-lg bg-[#163D8F] px-4 py-2 text-sm font-medium text-white hover:bg-[#0F2E72]"
                  onClick={() => setOpenFeedbackModal(true)}
                >
                  + Add Feedback
                </button>
              )}
            </div>

            {/* Timeline */}
            {allComments?.length > 0 ? (
              <div className="relative pl-8">
                <div className="absolute left-4 top-0 h-full w-px bg-slate-200" />

                {visibleFeedbacks.map((item) => (
                  <div key={item._id} className="relative mb-6">
                    <div className="absolute -left-[22px] top-8 h-4 w-4 rounded-full border-4 border-white bg-[#163D8F] shadow" />

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md">
                      <div className="flex items-start justify-between">
                        <div className="flex gap-3">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#163D8F] text-sm font-semibold text-white">
                            {item?.commentedBy?.name?.charAt(0)?.toUpperCase()}
                          </div>

                          <div>
                            <h3 className="font-semibold text-slate-800">
                              {item?.commentedBy?.name || "Unknown User"}
                            </h3>

                            <p className="text-xs uppercase tracking-wide text-slate-500">
                              {ROLE_MAP[String(item?.commentedBy?.role)] ||
                                "Internal Feedback"}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                          <span
                            className={`rounded-full px-4 py-1 text-xs font-semibold ${
                              item?.commentTone === "Positive"
                                ? "bg-green-100 text-green-700"
                                : item?.commentTone === "Neutral"
                                  ? "bg-blue-100 text-blue-700"
                                  : item?.commentTone === "Needs Improvement"
                                    ? "bg-orange-100 text-orange-700"
                                    : "bg-red-100 text-red-700"
                            }`}
                          >
                            {item?.commentTone}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 rounded-xl bg-slate-50 p-4">
                        <p className="leading-7 text-slate-700">
                          {item?.comment}
                        </p>
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-xs font-medium text-slate-500">
                          {new Date(item?.createdAt).toLocaleString()}
                        </div>

                        {role == -1 && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditComment(item)}
                              className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                            >
                              <ModeEditOutlinedIcon />
                            </button>

                            <button
                              disabled={commentLoading}
                              onClick={() => handleDeleteComment(item)}
                              className="rounded-md border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
                            >
                              <DeleteOutlineOutlined />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-3xl">
                  💬
                </div>

                <h3 className="text-lg font-semibold text-slate-700">
                  No Feedback Available
                </h3>

                <p className="mt-2 max-w-md text-sm text-slate-500">
                  No feedback has been added yet. Start by adding the first
                  performance review or internal comment for this vendor.
                </p>
              </div>
            )}
            {(allComments?.length || 0) > 3 && (
              <div className="flex justify-center pt-2">
                <button
                  onClick={() => setShowAllFeedbacks(!showAllFeedbacks)}
                  className="flex items-center gap-2 text-sm font-medium text-[#163D8F] hover:text-[#0F2E72]"
                >
                  {showAllFeedbacks ? (
                    <>
                      View Less Feedback
                      <span>▲</span>
                    </>
                  ) : (
                    <>
                      View Older Feedback
                      <span>▼</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
        {activeTab === "Installation Flag" && (
          <div className=" rounded-xl border h-[calc(100vh-200px)] overflow-auto border-[#D4D8E1] bg-white">
            <div className="">
              <table className={styles.campaignTable}>
                <thead>
                  <tr>
                    <th className={styles.campaignTableHeader}>Ticket ID</th>
                    <th className={styles.campaignTableHeader}>Campaign </th>

                    <th className={styles.campaignTableHeader}>Store</th>

                    <th className={styles.campaignTableHeader}> Product </th>
                    <th className={styles.campaignTableHeaderCenter}>Issue</th>
                    <th className={styles.campaignTableHeaderCenter}>
                      Raised On
                    </th>
                    <th className={styles.campaignTableHeaderCenter}>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {ticketsLoading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-16">
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#D4D8E1] border-t-[#4A6088]" />

                          <p className="mt-4 text-sm text-slate-500">
                            Loading installation tickets...
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : tickets?.length > 0 ? (
                    tickets.map((ticket, index) => {
                      return (
                        <tr
                          key={ticket._id}
                          className={`${styles.campaignTableRow} cursor-pointer`}
                          onClick={() => {
                            setFlagData(ticket);
                            setResolvedFlagModal(true);
                          }}
                        >
                          {/* NEW: Campaign ID cell */}
                          <td className={styles.campaignTableCell}>
                            <h3 className="text-[16px] font-medium text-[#1F2937]">
                              {ticket?.ticketNumber || "N/A"}
                            </h3>
                          </td>

                          <td className={styles.campaignTableCell}>
                            <div>
                              <h3 className="text-[16px] font-medium text-[#1F2937]">
                                {ticket?.campaignTitle || "N/A"}
                              </h3>
                            </div>
                          </td>

                          <td className={styles.campaignTableArea}>
                            {ticket?.storeName || "N/A"}
                          </td>

                          <td className={styles.campaignTableArea}>
                            {ticket?.productType || "N/A"}
                          </td>

                          <td className={styles.campaignTableValue}>
                            {ticket?.flagIssue}
                          </td>

                          <td className={styles.campaignTableCellCenter}>
                            <div>
                              <h3 className="text-[16px] font-medium text-[#1F2937]">
                                {formatIndianDateTime(ticket?.createdAt) ||
                                  "N/A"}
                              </h3>

                              <p className="mt-1 text-sm text-[#6B7280]">
                                {ticket?.flaggedBy?.name || "N/A"}
                              </p>
                            </div>
                          </td>

                          <td className={styles.campaignTableCellCenter}>
                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
                                ticket.status === "Resolved"
                                  ? "bg-[#A7F3D0] text-[#047857]"
                                  : "bg-[#FEE2E2] text-[#DC2626]"
                              }`}
                            >
                              {CapitalizeFirstLetter(ticket?.status)}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8} className="px-6 py-16">
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-3xl">
                            📂
                          </div>

                          <h3 className="text-lg font-semibold text-slate-700">
                            No Ticket History Found
                          </h3>

                          <p className="mt-2 max-w-md text-sm text-slate-500">
                            No installation tickets have been raised for this
                            vendor yet.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* {activeTab === "Rates" && (
          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <input
                type="text"
                placeholder="Search rates by product or campaign..."
                className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#163D8F]"
              />
            </div>

            {rateCards.map((campaign) => (
              <div
                key={campaign.id}
                className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
              >
                <div
                  onClick={() => handleAccordion(campaign.id)}
                  className="flex cursor-pointer items-center justify-between bg-slate-50 px-5 py-4"
                >
                  <div className="flex items-center gap-3">
                    {expanded.includes(campaign.id) ? (
                      <KeyboardArrowDownIcon />
                    ) : (
                      <KeyboardArrowRightOutlined />
                    )}

                    <h3 className="font-semibold text-slate-800">
                      {campaign.title}
                    </h3>

                    <span
                      className={`rounded px-2 py-1 text-[10px] font-semibold ${
                        campaign.status === "ONGOING"
                          ? "bg-blue-100 text-blue-700"
                          : campaign.status === "ACTIVE"
                            ? "bg-amber-100 text-amber-700"
                            : "bg-green-100 text-green-700"
                      }`}
                    >
                      {campaign.status}
                    </span>
                  </div>

                  <div className="text-xs text-slate-500">
                    Last Updated: {campaign.updatedAt}
                  </div>
                </div>

                {expanded.includes(campaign.id) && (
                  <>
                    <div className="grid grid-cols-12 border-y bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <div className="col-span-6">Product Details</div>
                      <div className="col-span-2">UOM</div>
                      <div className="col-span-2">Settled Rate</div>
                      <div className="col-span-2 text-center">Actions</div>
                    </div>

                    {campaign.products.map((product, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-12 items-center border-b last:border-0 px-5 py-4 hover:bg-slate-50"
                      >
                        <div className="col-span-6">
                          <h4 className="font-medium text-slate-800">
                            {product.name}
                          </h4>

                          <p className="text-sm text-slate-500">
                            {product.description}
                          </p>
                        </div>

                        <div className="col-span-2 text-slate-700">
                          {product.uom}
                        </div>

                        <div className="col-span-2 font-semibold text-[#163D8F]">
                          ₹{product.rate.toLocaleString()}
                        </div>

                        <div className="col-span-2 flex justify-center">
                          <button className="rounded-md border border-slate-200 p-2 hover:bg-white">
                            <MoreVertIcon fontSize="small" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ))}
          </div>
        )} */}
        {activeTab !== "Overview" &&
          activeTab !== "History" &&
          activeTab !== "Feedback & Document" &&
          activeTab !== "Rates" &&
          activeTab !== "Capabilities" &&
          activeTab !== "Installation Flag" && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-10 text-center text-slate-400">
              <div className="text-4xl mb-3">📋</div>
              <div className="text-sm font-medium">
                {activeTab} content coming soon
              </div>
            </div>
          )}
      </div>
      {selectedImage && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/20 backdrop-blur-sm "
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -right-3 -top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white text-black shadow"
            >
              ✕
            </button>

            <img
              src={selectedImage}
              alt="Preview"
              className="max-h-[90vh] max-w-full rounded-xl object-contain"
            />
          </div>
        </div>
      )}
      <AddFeedbackModal
        open={openFeedbackModal}
        onClose={() => setOpenFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        loading={commentLoading}
        editingCommentId={editingCommentId}
        feedback={feedback}
        setFeedback={setFeedback}
        tone={tone}
        setTone={setTone}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <Dialog open={resolvedFlagModal} maxWidth="md" fullWidth>
        <ResolveInstallation
          data={flagData}
          onClose={handleCloseResolvedFlag}
          setLoading={setLoading}
          loading={loading}
          vendorView={true}
        />
      </Dialog>
    </div>
  );
}
